<template>
  <div id="video-and-editor-view-container" class="d-flex flex-row">
    <resizable-frame
      right
      ref="videoPlayerResizableFrame"
      v-bind:width-px.sync="$_videoPlayerWidthPx"
      v-bind:width-px-max="$data.$_videoPlayerWidthPxMax"
      v-bind:width-px-min="$_videoPlayerWidthPxMin"
    >
      <video-player
        ref="videoPlayer"
        v-bind:video-title="videoTitle"
        v-bind:video-url="videoUrl"
        v-bind:video-time-range="videoTimeRange"
        v-bind:video-playback-rate.sync="$_videoPlaybackRate"
        v-bind:video-volume-rate.sync="$_videoVolumeRate"
        v-bind:video-muted.sync="$_videoMuted"
        v-bind:is-loop-enabled.sync="$_isLoopEnabled"
        v-bind:loop-definition="loopDefinition"
        v-bind:sorted-marker-segments="$_sortedMarkerSegments"
        v-bind:audio-only="audioOnly"
        v-on:meta-data-loaded="$_onMetaDataLoaded"
        v-on:play-info-updated="$_onPlayInfoUpdated"
        v-on:playback-fixed="$_onPlaybackFixed"
      >
      </video-player>
    </resizable-frame>
    <editor-view
      class="flex-grow-1"
      v-bind:timeline-data-set="timelineDataSet"
      v-bind:selected-timeline-segment-ids="selectedTimelineSegmentIds"
      v-bind:annotator-id="annotatorId"
      v-bind:annotator-name="annotatorName"
      v-bind:editor-definitions="editorDefinitions"
    >
    </editor-view>
  </div>
</template>

<style scoped>
#video-and-editor-view-container {
  width: 100%;
  height: 100%;
}
</style>

<script>
import VideoPlayer from './VideoPlayer.vue';
import EditorView from './EditorView.vue';
import ResizableFrame from './Parts/ResizableFrame.vue';
import { TimelineDataSet } from '../modules/ScattData.js';
import LoopDefinition from '../modules/LoopDefinition.js';
import TimelineMarker from '../modules/TimelineMarker.js';
import VideoTimeRange from './VideoPlayer/modules/VideoTimeRange.js';
import MarkerSegmentData from '../modules/segmentDataTypes/MarkerSegmentData.js';
import TimeValue from '../modules/TimeValue.js';
import utils from '../modules/utils.js';

const minimumPlayTime = TimeValue.zero;
const videoPlayerWidthPxMin = 100;
const editorViewWidthPxMin = 100;

export default {
  components: {
    VideoPlayer,
    EditorView,
    ResizableFrame,
  },

  watch: {
    videoPlayerWidthPx(videoPlayerWidthPx) {
      this.$_updateVideoPlayerWidthPx(videoPlayerWidthPx);
    },

    '$data.$_videoPlayerWidthPxMax'() {
      this.$_updateVideoPlayerWidthPx(this.videoPlayerWidthPx);
    },
  },

  props: {
    timelineDataSet:            { type: TimelineDataSet },
    videoTitle:                 { type: String },
    videoUrl:                   { type: String },
    videoTimeRange:             { type: VideoTimeRange },
    videoPlaybackRate:          { type: Number },
    videoVolumeRate:            { type: Number },
    videoMuted:                 { type: Boolean },
    isLoopEnabled:              { type: Boolean },
    loopDefinition:             { type: LoopDefinition },
    timelineMarker:             { type: TimelineMarker },
    audioOnly:                  { type: Boolean },
    videoPlayerWidthPx:         { type: Number },
    selectedTimelineSegmentIds: { type: Array },
    annotatorId:                { type: String },
    annotatorName:              { type: String },
    editorDefinitions:          { type: Array },
  },

  data() {
    return {
      $_videoAndEditorViewContainerResizeObserver: new ResizeObserver(this.$_onResize),
      $_videoPlayerWidthPxMax: 0,
    };
  },

  computed: {
    $_videoPlaybackRate: {
      get()                  { return this.videoPlaybackRate },
      set(videoPlaybackRate) { this.$emit('update:video-playback-rate', videoPlaybackRate) }
    },

    $_videoVolumeRate: {
      get()                { return this.videoVolumeRate },
      set(videoVolumeRate) { this.$emit('update:video-volume-rate', videoVolumeRate) }
    },

    $_videoMuted: {
      get()           { return this.videoMuted },
      set(videoMuted) { this.$emit('update:video-muted', videoMuted) }
    },

    $_isLoopEnabled: {
      get()              { return this.isLoopEnabled },
      set(isLoopEnabled) { this.$emit('update:is-loop-enabled', isLoopEnabled) }
    },

    $_sortedMarkerSegments() {
      let sortedMarkerSegments = [];
      if (this.timelineMarker !== undefined) {
        sortedMarkerSegments.push(new MarkerSegmentData(
          (this.timelineMarker.begin === null)? null : this.timelineMarker.begin.clone(),
          (this.timelineMarker.end === null)? null : this.timelineMarker.end.clone(),
          new MarkerSegmentData.contentData('Temporal Marker'),
          false,
        ));
      }
      for (let timelineData of Object.values(this.timelineDataSet)) {
        if (timelineData.segmentDataType === MarkerSegmentData) {
          sortedMarkerSegments.push(...Object.values(timelineData.segments));
        }
      }
      return sortedMarkerSegments.sort((segmentA, segmentB) => {
        let physicalSegmentABegin = (segmentA.begin === null)? minimumPlayTime : segmentA.begin;
        let physicalSegmentBBegin = (segmentB.begin === null)? minimumPlayTime : segmentB.begin;
        return physicalSegmentABegin.getOffsetFrom(physicalSegmentBBegin).msec;
      });
    },

    $_videoPlayerWidthPxMin() { return videoPlayerWidthPxMin },

    $_videoPlayerWidthPx: {
      get()                   { return this.videoPlayerWidthPx },
      set(videoPlayerWidthPx) { this.$_updateVideoPlayerWidthPx(videoPlayerWidthPx) },
    },
  },

  mounted() {
    this.$data.$_videoAndEditorViewContainerResizeObserver.observe(this.$el);
  },

  destroyed() {
    this.$data.$_videoAndEditorViewContainerResizeObserver.disconnect();
  },

  methods: {
    /* public */
    onMousemove(event) {
      this.$refs.videoPlayerResizableFrame.onMousemove(event);
      this.$refs.videoPlayer.onMousemove(event);
    },

    onMouseup(event) {
      this.$refs.videoPlayerResizableFrame.onMouseup(event);
    },

    onKeydown(event) {
      {
        switch (event.code) {
        case 'BracketLeft':
          shrinkVideoArea.call(this);
          return true;
        case 'BracketRight':
          spreadVideoArea.call(this);
          return true;
        }
        return false;
      }

      function shrinkVideoArea() {
        let videoPlayerWidthPx = this.videoPlayerWidthPx - 50;
        if (videoPlayerWidthPx < videoPlayerWidthPxMin) {
          videoPlayerWidthPx = videoPlayerWidthPxMin;
        }
        this.$_updateVideoPlayerWidthPx(videoPlayerWidthPx);
      }

      function spreadVideoArea() {
        let videoPlayerWidthPx = this.videoPlayerWidthPx + 50;
        if (videoPlayerWidthPx > this.videoPlayerWidthPxMax) {
          videoPlayerWidthPx = this.videoPlayerWidthPxMax;
        }
        this.$_updateVideoPlayerWidthPx(videoPlayerWidthPx);
      }
    },

    seek(playTime) { this.$refs.videoPlayer.seek(playTime) },

    async togglePlayAndPause() { await this.$refs.videoPlayer.togglePlayAndPause() },

    /* private */
    $_onMetaDataLoaded(metaData) { this.$emit('meta-data-loaded', metaData) },

    $_onPlayInfoUpdated(playInfo) { this.$emit('play-info-updated', playInfo) },

    $_onPlaybackFixed(begin, end) { this.$emit('playback-fixed', begin, end) },

    $_onResize(resizeObserverEntries) {
      let videoAndEditorViewContainerWidth = resizeObserverEntries[0].target.clientWidth;
      let videoPlayerWidthPxMax = videoAndEditorViewContainerWidth - editorViewWidthPxMin;
      this.$data.$_videoPlayerWidthPxMax = videoPlayerWidthPxMax;
    },

    $_updateVideoPlayerWidthPx(videoPlayerWidthPx) {
      this.$emit(
        'update:video-player-width-px',
        utils.clamp(
          videoPlayerWidthPx,
          videoPlayerWidthPxMin,
          this.$data.$_videoPlayerWidthPxMax,
        ),
      );
    }
  },
}
</script>